import { IconButton } from '@design-system/components/button/iconButton';
import {
  faCaretLeft,
  faCaretRight,
  faCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactNode, useState } from 'react';
import { clsx } from 'clsx';
import { sprinkles } from '@design-system/sprinkles.css';
import { Typography } from '@design-system/components/typography/typography';
import { Modal } from '@design-system/components/modal/modal';
import { Tag } from '@design-system/components/tag/tag';
import { Button } from '@design-system/components/button/button';
import guideStepLearnImage from '/static/images/guide-step-learn.png';
import guideStepStartImage from '/static/images/guide-step-start.png';

type StepValues = 0 | 1 | 2 | 3;

type PaidProgramGuideProps = {
  isOpen: boolean;
  close: () => void;
  initStep?: StepValues;
};

export const PaidProgramGuide = ({
  isOpen,
  close,
  initStep,
}: PaidProgramGuideProps) => {
  const [step, setStep] = useState<StepValues>(initStep ?? 0);
  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={getTitleByStep(step)}
      modalClassName="w-[900px]"
      contentClassName="gap-[39px]"
    >
      <div className="flex flex-col gap-[39px]">
        <div className="flex gap-2 h-[350px]">
          <IconButton
            icon={faCaretLeft}
            variant="text"
            className="h-full"
            onClick={() =>
              step > 0
                ? setStep((prev) => (prev - 1) as StepValues)
                : setStep(3)
            }
          />
          <div className="w-full h-full">
            <Steps step={step} />
          </div>
          <IconButton
            icon={faCaretRight}
            variant="text"
            className="h-full"
            onClick={() =>
              step < 3
                ? setStep((prev) => (prev + 1) as StepValues)
                : setStep(0)
            }
          />
        </div>
        <StepIndicator active={step} change={(value) => setStep(value)} />
        <div className="flex justify-center" onClick={close}>
          <Button color="neutral">Ok, I'm ready</Button>
        </div>
      </div>
    </Modal>
  );
};

const StepIndicator = ({
  active,
  change,
}: {
  active: StepValues;
  change: (value: StepValues) => void;
}) => (
  <div className="flex gap-2.5 items-center justify-center">
    {Array.from({ length: 4 }, (_, i) => (
      <FontAwesomeIcon
        icon={faCircle}
        key={i}
        className={clsx(
          'cursor-pointer',
          active === i && sprinkles({ color: 'P-80', fontSize: 'm' }),
          active !== i && sprinkles({ color: 'N-60', fontSize: 'xs' }),
        )}
        onClick={() => change(i as StepValues)}
      />
    ))}
  </div>
);

const getTitleByStep = (step: 0 | 1 | 2 | 3): string => {
  switch (step) {
    case 0:
      return 'Quick Guide to Packaging & Selling a Paid program';
    case 1:
      return 'Figuring out what program to start with';
    case 2:
      return 'Variations & pricing';
    case 3:
      return 'Learn from others';
  }
};

const Steps = ({ step }: { step: 0 | 1 | 2 | 3 }): ReactNode => {
  switch (step) {
    case 0:
      return (
        <div className="flex gap-6 justify-center items-center h-full">
          <img src={guideStepStartImage} width={260} height={420}></img>
          <ul className="list-disc">
            {Step1.map((i) => (
              <li className="mb-6" key={i}>
                {i}
              </li>
            ))}
          </ul>
        </div>
      );
    case 1:
      return (
        <div className="flex flex-col gap-[56px] h-full">
          <Typography variant="body" size="large">
            We recommend starting cheaper to test the audience. Increase price
            as you get more data. Think of these questions ahead to decide
          </Typography>
          <div
            className={clsx('p-6 rounded-[10px]', sprinkles({ bg: 'P-20' }))}
          >
            <ul className={clsx('list-["👉"] ml-6', sprinkles({ bg: 'P-20' }))}>
              {Step2.map((i) => (
                <li key={i}>
                  <Typography className="pl-2 mb-4" variant="body" size="large">
                    {i}
                  </Typography>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    case 2:
      return (
        <div className="flex flex-col gap-4 overflow-y-scroll h-[345px]">
          {Step3Variations.map((v) => (
            <div
              key={v.title}
              className={clsx(
                'flex flex-col p-3 gap-4 rounded-[10px]',
                sprinkles({ bg: 'N-10' }),
              )}
            >
              <div className="flex justify-between items-center">
                <Typography variant="headline" size="small">
                  {v.title}
                </Typography>
                <div className="flex gap-2">
                  {v.recommendation ? (
                    <Tag
                      label="BEST TO START"
                      className={sprinkles({
                        bg: 'SUCCESS-100',
                        color: 'W-100',
                      })}
                      readonly={true}
                    />
                  ) : null}
                  {v.tags.map((t) => (
                    <Tag
                      key={t}
                      label={t}
                      className={sprinkles({ bg: 'P-20' })}
                      readonly={true}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-4 justify-start items-center">
                <div className="max-w-[50%]">
                  <Typography variant="body" size="large">
                    {v.details}
                  </Typography>
                </div>
                <div className="flex flex-col max-w-[50%]">
                  <Typography color="P-80">BUNDLE WITH:</Typography>
                  <Typography>{v.bundle}</Typography>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    case 3:
      return (
        <div className="flex items-center justify-center">
          <img src={guideStepLearnImage} height={400}></img>
        </div>
      );
  }
};

const Step2 = [
  'Have you sold anything in the page? How did that go?',
  'What do your followers and consumers regularly ask to see from you?',
  'What is thedemographic of your audience? Male/female, young/old?',
  'Do you want to have a nutrition component?',
  'What type of time commitment do you want from your users?',
];

const Step1 = [
  'Great for getting started when new to digital sales',
  'They have ashelf-life and sales gradually decline over the long-run',
  'Launch witha paid program and offer a discount a few months later',
];

const Step3Variations = [
  {
    title: 'Lead Gen',
    details:
      'Good for testing your audience to see how interested they are in purchasing your programs.',
    bundle: 'Recipes sold individually or not included in the program.',
    recommendation: true,
    tags: ['1-2 weeks', '$0-5'],
  },
  {
    title: 'Low-Ticket',
    details: 'This is a quality program that your consumers are eager for.',
    bundle: "Doesn't need nutrition if you don't want to offer.",
    tags: ['2-4 weeks', '$5-50'],
  },
  {
    title: 'High-Ticket',
    details:
      'In-depth programs targeting a specific audience who knows exactly what they are purchasing and understand the value.',
    bundle: 'Full meal plan with recipes, grocery list, and daily schedule.',
    tags: ['2-4 weeks', '$5-50'],
  },
  {
    title: 'Challenge',
    details:
      'Sell your program as normal and have this purchase be considered a "challenge entry fee" making a user eligible to win prizes.',
    bundle: 'Nutrition coaching videos can be included in the program.',
    tags: ['2-4 weeks', '$5-50'],
  },
];
